import { useEffect, useState, useCallback } from "react";
import { useParams, useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { toast } from "sonner";
import {
  ArrowLeft, Loader2, Sparkles, RefreshCw, Clock, MessageSquare, ShieldAlert,
  Send, Lock, Calendar, Mail, User, Target, Users2,
} from "lucide-react";
import Layout from "@/components/Layout";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Switch } from "@/components/ui/switch";
import { Label } from "@/components/ui/label";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { StatusBadge, CategoryBadge, PriorityBadge } from "@/components/Badges";
import api, { formatApiErrorDetail } from "@/lib/api";
import { STATUSES, PRIORITIES, CATEGORY_MAP } from "@/lib/constants";

const fmt = (iso) => new Date(iso).toLocaleString(undefined, { dateStyle: "medium", timeStyle: "short" });

export default function CaseDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [c, setC] = useState(null);
  const [specialists, setSpecialists] = useState([]);
  const [note, setNote] = useState("");
  const [internal, setInternal] = useState(true);
  const [sending, setSending] = useState(false);
  const [retriaging, setRetriaging] = useState(false);
  const [error, setError] = useState(false);

  const load = useCallback(() => {
    api.get(`/concerns/${id}`).then((r) => setC(r.data)).catch(() => setError(true));
  }, [id]);

  useEffect(() => {
    load();
    api.get("/specialists").then((r) => setSpecialists(r.data)).catch(() => {});
  }, [load]);

  const patch = async (body, msg) => {
    try {
      await api.patch(`/concerns/${id}`, body);
      load();
      if (msg) toast.success(msg);
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Update failed.");
    }
  };

  const addNote = async () => {
    if (!note.trim()) return;
    setSending(true);
    try {
      await api.post(`/concerns/${id}/notes`, { content: note, internal });
      setNote("");
      load();
      toast.success("Note added.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Could not add note.");
    } finally {
      setSending(false);
    }
  };

  const retriage = async () => {
    setRetriaging(true);
    try {
      await api.post(`/concerns/${id}/retriage`);
      load();
      toast.success("AI triage refreshed.");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Triage failed.");
    } finally {
      setRetriaging(false);
    }
  };

  if (error) return <Layout showFooter={false}><div className="max-w-4xl mx-auto px-5 py-20 text-charcoal-600">Case not found.</div></Layout>;
  if (!c) return <Layout showFooter={false}><div className="py-32 flex justify-center"><Loader2 className="h-6 w-6 animate-spin text-forest-600" /></div></Layout>;

  const t = c.ai_triage || {};

  return (
    <Layout showFooter={false}>
      <div className="max-w-6xl mx-auto px-5 sm:px-8 py-10">
        <button onClick={() => navigate("/admin")} data-testid="case-back-btn" className="inline-flex items-center gap-1.5 text-sm text-charcoal-600 hover:text-forest-800 mb-6">
          <ArrowLeft className="h-4 w-4" strokeWidth={1.5} /> Back to cases
        </button>

        <div className="flex flex-wrap items-center gap-2">
          <span className="text-xs font-semibold text-charcoal-600">{c.case_number}</span>
          <StatusBadge status={c.status} />
          <PriorityBadge priority={c.priority} />
          <CategoryBadge category={c.category} />
          {c.is_anonymous && <span className="text-[11px] rounded-full bg-bone-100 px-2.5 py-0.5 text-charcoal-600">Anonymous</span>}
        </div>
        <h1 className="mt-3 font-serif text-3xl sm:text-4xl text-forest-900 leading-tight">{c.title}</h1>
        <p className="mt-2 text-sm text-charcoal-600">Submitted {fmt(c.created_at)} • Last updated {fmt(c.updated_at)}</p>

        <div className="mt-8 grid lg:grid-cols-3 gap-6">
          {/* Main */}
          <div className="lg:col-span-2 space-y-6">
            <section className="bg-white rounded-2xl border border-forest-600/10 p-6 shadow-sm">
              <h2 className="font-serif text-lg text-forest-900">Concern details</h2>
              <p className="mt-3 text-charcoal-700 leading-relaxed whitespace-pre-wrap">{c.description}</p>
              <div className="mt-5 grid sm:grid-cols-2 gap-4 text-sm">
                {!c.is_anonymous && (
                  <>
                    <Detail icon={User} label="Reporter" value={c.reporter_name || "—"} />
                    <Detail icon={Mail} label="Email" value={c.reporter_email || "—"} />
                  </>
                )}
                <Detail icon={ShieldAlert} label="Campus" value={c.campus || "—"} />
                {c.desired_outcome && <Detail icon={Target} label="Desired outcome" value={c.desired_outcome} />}
                {c.involves_others && <Detail icon={Users2} label="Others involved" value={c.involves_others} />}
                {c.request_meeting && <Detail icon={Calendar} label="Facilitated conversation" value={`Requested (${c.meeting_mode || "no preference"})`} />}
              </div>
            </section>

            {/* AI Triage */}
            <section className="rounded-2xl border border-gold/30 bg-bone-100/70 p-6">
              <div className="flex items-center justify-between">
                <div className="inline-flex items-center gap-2">
                  <Sparkles className="h-5 w-5 text-gold" strokeWidth={1.5} />
                  <h2 className="font-serif text-lg text-forest-900">AI Triage Assessment</h2>
                </div>
                <Button variant="ghost" size="sm" onClick={retriage} disabled={retriaging} data-testid="case-retriage-btn" className="rounded-full text-forest-700 hover:bg-forest-50">
                  {retriaging ? <Loader2 className="h-4 w-4 animate-spin" /> : <><RefreshCw className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Re-run</>}
                </Button>
              </div>
              {t.generated ? (
                <div className="mt-4 space-y-4">
                  <p className="text-charcoal-700 leading-relaxed">{t.summary}</p>
                  <div className="flex flex-wrap gap-2">
                    <Chip label={`Suggested: ${CATEGORY_MAP[t.category]?.label || t.category}`} />
                    <Chip label={`Priority: ${t.priority}`} />
                    <Chip label={`Confidence: ${Math.round((t.confidence || 0) * 100)}%`} />
                  </div>
                  {t.policy_references?.length > 0 && (
                    <div>
                      <p className="text-xs uppercase tracking-wide text-charcoal-600 mb-1.5">Relevant policies</p>
                      <div className="flex flex-wrap gap-2">
                        {t.policy_references.map((p) => (
                          <span key={p} className="rounded-full bg-forest-600 text-bone text-xs font-semibold px-3 py-1">{p}</span>
                        ))}
                      </div>
                    </div>
                  )}
                  {t.recommended_next_step && (
                    <div className="rounded-xl bg-white border border-forest-600/10 p-4">
                      <p className="text-xs uppercase tracking-wide text-terracotta-500 font-bold mb-1">Recommended next step</p>
                      <p className="text-sm text-charcoal-700">{t.recommended_next_step}</p>
                    </div>
                  )}
                </div>
              ) : (
                <p className="mt-3 text-sm text-charcoal-600">{t.summary || "Automated triage is unavailable. Review manually or re-run."}</p>
              )}
            </section>

            {/* Timeline */}
            <section className="bg-white rounded-2xl border border-forest-600/10 p-6 shadow-sm">
              <h2 className="font-serif text-lg text-forest-900">Investigation timeline</h2>
              <ol className="mt-5 space-y-4 border-l-2 border-forest-100 pl-5">
                {c.timeline?.map((ev) => (
                  <li key={ev.id} className="relative">
                    <span className="absolute -left-[26px] top-0.5 grid place-items-center h-6 w-6 rounded-full bg-forest-50 text-forest-600 ring-4 ring-white">
                      {ev.type === "note" ? <MessageSquare className="h-3.5 w-3.5" strokeWidth={1.5} /> : <Clock className="h-3.5 w-3.5" strokeWidth={1.5} />}
                    </span>
                    <div className="flex items-center gap-2">
                      <p className="text-sm text-charcoal-700">{ev.content}</p>
                      {ev.internal && <span className="inline-flex items-center gap-1 text-[10px] rounded-full bg-bone-100 px-2 py-0.5 text-charcoal-600"><Lock className="h-3 w-3" /> Internal</span>}
                    </div>
                    <p className="text-xs text-charcoal-600 mt-0.5">{ev.author_name} • {fmt(ev.created_at)}</p>
                  </li>
                ))}
              </ol>

              <div className="mt-6 border-t border-forest-600/10 pt-4">
                <Textarea value={note} onChange={(e) => setNote(e.target.value)} data-testid="case-note-input"
                  placeholder="Add a case note or update…" rows={3} className="bg-bone rounded-lg border-forest-600/15 resize-none" />
                <div className="mt-3 flex items-center justify-between">
                  <label className="flex items-center gap-2 text-sm text-charcoal-700 cursor-pointer">
                    <Switch checked={internal} onCheckedChange={setInternal} data-testid="case-note-internal-toggle" />
                    <span className="inline-flex items-center gap-1"><Lock className="h-3.5 w-3.5" strokeWidth={1.5} /> Internal only</span>
                  </label>
                  <Button onClick={addNote} disabled={sending || !note.trim()} data-testid="case-note-submit" className="rounded-full bg-forest-600 hover:bg-forest-700 text-bone">
                    {sending ? <Loader2 className="h-4 w-4 animate-spin" /> : <><Send className="h-4 w-4 mr-1.5" strokeWidth={1.5} /> Add note</>}
                  </Button>
                </div>
                {!internal && <p className="mt-1.5 text-xs text-terracotta-500">This note will be visible to the reporter.</p>}
              </div>
            </section>
          </div>

          {/* Controls sidebar */}
          <aside className="space-y-5">
            <motion.div initial={{ opacity: 0, x: 12 }} animate={{ opacity: 1, x: 0 }} className="bg-white rounded-2xl border border-forest-600/10 p-6 shadow-sm sticky top-20">
              <h3 className="font-serif text-lg text-forest-900">Manage case</h3>
              <div className="mt-4 space-y-4">
                <div>
                  <Label className="text-forest-900 font-semibold text-sm">Status</Label>
                  <Select value={c.status} onValueChange={(v) => patch({ status: v }, "Status updated.")}>
                    <SelectTrigger data-testid="case-status-select" className="mt-1.5 bg-bone rounded-lg border-forest-600/15 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{STATUSES.map((s) => <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-forest-900 font-semibold text-sm">Priority</Label>
                  <Select value={c.priority} onValueChange={(v) => patch({ priority: v }, "Priority updated.")}>
                    <SelectTrigger data-testid="case-priority-select" className="mt-1.5 bg-bone rounded-lg border-forest-600/15 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>{PRIORITIES.map((p) => <SelectItem key={p.value} value={p.value}>{p.label}</SelectItem>)}</SelectContent>
                  </Select>
                </div>
                <div>
                  <Label className="text-forest-900 font-semibold text-sm">Assigned specialist</Label>
                  <Select value={c.assigned_to || "unassigned"} onValueChange={(v) => patch({ assigned_to: v === "unassigned" ? "" : v }, "Assignment updated.")}>
                    <SelectTrigger data-testid="case-assign-select" className="mt-1.5 bg-bone rounded-lg border-forest-600/15 h-11"><SelectValue /></SelectTrigger>
                    <SelectContent>
                      <SelectItem value="unassigned">Unassigned</SelectItem>
                      {specialists.map((s) => <SelectItem key={s.id} value={s.id}>{s.name}</SelectItem>)}
                    </SelectContent>
                  </Select>
                </div>
              </div>
            </motion.div>
          </aside>
        </div>
      </div>
    </Layout>
  );
}

function Detail({ icon: Icon, label, value }) {
  return (
    <div className="flex items-start gap-2.5">
      <Icon className="h-4 w-4 text-forest-500 mt-0.5 shrink-0" strokeWidth={1.5} />
      <div>
        <p className="text-xs uppercase tracking-wide text-charcoal-600">{label}</p>
        <p className="text-charcoal-800">{value}</p>
      </div>
    </div>
  );
}

function Chip({ label }) {
  return <span className="rounded-full bg-white border border-forest-600/15 text-forest-800 text-xs font-semibold px-3 py-1">{label}</span>;
}
